import { z } from 'zod';

export const NOTICE_TITLE_MAX_LENGTH = 100;
export const NOTICE_DESCRIPTION_MAX_LENGTH = 280;

const INCIDENT_REFERENCE_PATTERN = /^(?:inc[-\s]?)?(\d{1,10})$/i;
const INCIDENT_REFERENCE_PREFIX = 'INC-';

export const INCIDENT_IO_INCIDENT_BASE_URL =
  'https://app.incident.io/neo4j/incidents/';

/**
 * Normalises an incident reference typed by a person (`745`, `inc-745`,
 * `INC 745`) to the `INC-745` form, or returns `undefined` if it is not one.
 */
export function normaliseIncidentId(value: string): string | undefined {
  const match = INCIDENT_REFERENCE_PATTERN.exec(value.trim());
  if (!match) {
    return undefined;
  }
  return `${INCIDENT_REFERENCE_PREFIX}${Number.parseInt(match[1], 10)}`;
}

export function incidentUrl(incidentId: string): string {
  const number = incidentId.replace(INCIDENT_REFERENCE_PREFIX, '');
  return `${INCIDENT_IO_INCIDENT_BASE_URL}${number}`;
}

export const noticeInputSchema = z.object({
  title: z
    .string()
    .max(
      NOTICE_TITLE_MAX_LENGTH,
      `Keep the title to ${NOTICE_TITLE_MAX_LENGTH} characters or fewer`,
    )
    .trim()
    .min(1, 'Enter a title'),
  description: z
    .string()
    .max(
      NOTICE_DESCRIPTION_MAX_LENGTH,
      `Keep the description to ${NOTICE_DESCRIPTION_MAX_LENGTH} characters or fewer`,
    )
    .trim()
    .min(1, 'Enter a description'),
  incidentId: z
    .string()
    .trim()
    .optional()
    .transform((value, ctx) => {
      if (!value) {
        return undefined;
      }
      const normalised = normaliseIncidentId(value);
      if (!normalised) {
        ctx.addIssue({
          code: 'custom',
          message: 'Enter an incident ID such as INC-745 or 745',
        });
        return z.NEVER;
      }
      return normalised;
    }),
});

export const noticeUpdateSchema = noticeInputSchema.pick({
  title: true,
  description: true,
});

export type NoticeFormInput = z.input<typeof noticeInputSchema>;
export type NoticeFormOutput = z.output<typeof noticeInputSchema>;
export type NoticeUpdateFormInput = z.input<typeof noticeUpdateSchema>;
export type NoticeUpdateFormOutput = z.output<typeof noticeUpdateSchema>;
